import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepSection } from '@/components/step-section';
import { ApiError } from '@/lib/api';
import { use_draft, use_submit_onboarding } from '@/features/onboarding/hooks';
import { module_steps_for_draft } from '@/features/onboarding/module-step-router';
import { use_session_store } from '@/lib/session-store';
import type { SubmitResponse } from '@/features/onboarding/types';
import { StatusPill } from '@/components/ui/status-pill';
import { kyc_presentation } from '@/features/vendor-profile/kyc-status';

export function SubmitPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const submit_m = use_submit_onboarding();
  const clear_session = use_session_store((s) => s.clear);
  const [error, set_error] = useState<string | null>(null);
  const [result, set_result] = useState<SubmitResponse | null>(null);

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.terms_completed) {
      navigate('/onboarding/terms', { replace: true });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  async function handle_submit() {
    set_error(null);
    try {
      const res = await submit_m.mutateAsync();
      set_result(res);
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Submit failed');
    }
  }

  function handle_done() {
    clear_session();
    navigate('/login', { replace: true });
  }

  if (result) {
    const kyc = kyc_presentation(result.kyc_status);
    return (
      <StepSection
        step_key="submit"
        title="Application sent"
        description="An admin reviews every new vendor before their listings go live."
      >
        {/*
          This block used to print `Vendor profile cmt6s4… → DOCUMENTS_SUBMITTED` and a count
          of "claimed" documents. That is the shape of the API response, not an answer to the
          only question a vendor has at this moment: am I approved, and when will I know?
        */}
        <div
          className="flex items-start gap-3 rounded-card border border-[hsl(var(--success)/0.25)] bg-[hsl(var(--success)/0.1)] p-4 text-foreground"
          data-testid="submit-success"
        >
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-success" />
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 font-semibold">
              {kyc.headline}
              <StatusPill tone={kyc.tone} data-testid="submit-kyc-status">
                {kyc.short}
              </StatusPill>
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{kyc.detail}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              You can set up your services and availability in the meantime — customers will
              be able to book you as soon as you are approved.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {result.kyc_docs_claimed} document{result.kyc_docs_claimed === 1 ? '' : 's'} and{' '}
              {result.photos_claimed} photo{result.photos_claimed === 1 ? '' : 's'} received.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="gradient" onClick={() => navigate('/vendor/app')} data-testid="goto-workspace">
            Go to your workspace
          </Button>
          <Button variant="outline" onClick={handle_done} data-testid="submit-done">
            Sign out
          </Button>
        </div>
      </StepSection>
    );
  }

  // Don't flash the live Submit view while we're still finding out whether
  // this application was already submitted.
  if (!draft_q.isFetched) {
    return (
      <StepSection step_key="submit" title="Review & submit" description="Loading your application…">
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking status…
        </div>
      </StepSection>
    );
  }

  const d = draft_q.data;

  // Returning vendor (refresh or re-login after submit): the application is
  // locked — show its status instead of a live Submit button.
  if (d?.submitted_at) {
    const submitted_on = new Date(d.submitted_at).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    return (
      <StepSection
        step_key="submit"
        title="Application submitted"
        description="Your profile is with the admin review team. We'll notify you once a decision is made — there's nothing more to do here."
      >
        <div
          className="flex items-center gap-3 rounded-md border border-success/25 bg-success/10 p-4 text-foreground"
          data-testid="submit-already"
        >
          <CheckCircle2 className="h-6 w-6 text-success" />
          <div>
            <p className="font-semibold">Submitted on {submitted_on}</p>
            <p className="text-xs">
              Modules: {d.module_types.join(', ')} · Your application can no longer be
              edited. Contact support if something needs to change.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="gradient" onClick={() => navigate('/vendor/app')} data-testid="goto-workspace">
            Go to your workspace
          </Button>
          <Button variant="outline" onClick={handle_done} data-testid="submit-done">
            Sign out
          </Button>
        </div>
      </StepSection>
    );
  }
  // Surface what the draft already tracks — otherwise a vendor can submit with
  // zero documents and half the module forms blank and only find out via admin.
  const kyc_doc_count = d?.kyc_doc_count ?? 0;
  const photo_count = d?.photo_count ?? 0;
  const pending_modules = module_steps_for_draft(d).filter(
    (m) => !d?.module_details_completed?.[m],
  );

  return (
    <StepSection
      step_key="submit"
      title="Review & submit"
      description="One last check before your profile heads to admin review."
      error={error}
      footer={
        <Button
          variant="gradient"
          onClick={handle_submit}
          disabled={submit_m.isPending}
          data-testid="submit-final"
        >
          {submit_m.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Submit for review
        </Button>
      }
    >
      <ul className="divide-y divide-border text-sm">
        <Row label="Modules" value={d?.module_types.join(', ') ?? '—'} />
        <Row label="Basic info" value={d?.basic_info_completed ? '✅ done' : '⚠️ missing'} />
        <Row label="Address" value={d?.address_completed ? '✅ done' : '⚠️ missing'} />
        <Row
          label="Module details"
          value={
            pending_modules.length === 0
              ? '✅ all done'
              : `⚠️ still to fill: ${pending_modules.join(', ')}`
          }
        />
        <Row
          label="Documents"
          value={
            kyc_doc_count > 0
              ? `✅ ${kyc_doc_count} uploaded`
              : '⚠️ none uploaded — admin will ask for these'
          }
        />
        <Row
          label="Photos"
          value={
            photo_count > 0
              ? `✅ ${photo_count} uploaded`
              : '⚠️ none uploaded — admin will ask for these'
          }
        />
        {/*
          Payout is REQUIRED to submit, so this row has to be here — without it a vendor who
          skipped the step reads a checklist of ticks, presses Submit and gets a 422 naming a
          step the page never mentioned.
        */}
        <Row
          label="Payout details"
          value={d?.payout_completed ? '✅ saved' : '⚠️ required — we have nowhere to pay you'}
        />
        <Row label="Terms" value={d?.terms_completed ? '✅ accepted' : '⚠️ not accepted'} />
      </ul>
      <p className="text-xs text-muted-foreground">
        Submitting sends your profile to the admin review team and attaches the documents and
        photos you uploaded. You won't be able to edit the application afterwards.
      </p>
    </StepSection>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <li className="grid grid-cols-3 gap-3 py-2">
      <span className="font-semibold text-muted-foreground">{label}</span>
      <span className="col-span-2">{value}</span>
    </li>
  );
}
